import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ROLE_BADGE = { seller:'badge--green', buyer:'badge--blue', agent:'badge--purple' };
const ROLE_LABEL = { seller:'Farmer', buyer:'Buyer', agent:'Agent' };

export default function ReferralsPage() {
  const [topReferrers, setTopReferrers] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [stats,        setStats]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [roleFilter,   setRoleFilter]   = useState('');
  const [expanded,     setExpanded]     = useState(null);
  const [referrals,    setReferrals]    = useState({});
  const [refLoading,   setRefLoading]   = useState('');
  const [exporting,    setExporting]    = useState(false);

  useEffect(() => { load(); }, [roleFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit:100 });
      if (roleFilter) params.set('role', roleFilter);
      const { data } = await api.get('/admin/referrals?' + params);
      setTopReferrers(data.referrers || []);
      setStats(data.stats || null);
    } catch { toast.error('Failed to load referrals'); }
    finally { setLoading(false); }
  };

  const loadReferrals = async (userId) => {
    if (referrals[userId]) {
      setExpanded(expanded === userId ? null : userId);
      return;
    }
    setRefLoading(userId);
    try {
      const { data } = await api.get(`/admin/users/${userId}/referrals`);
      setReferrals(p => ({ ...p, [userId]: data.referrals || [] }));
      setExpanded(userId);
    } catch { toast.error('Failed to load'); }
    finally { setRefLoading(''); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/admin/referrals?limit=1000');
      const rows = (data.referrers || []).map(u => ({
        Name:          u.name || '—',
        Mobile:        '+91 ' + u.mobile,
        Role:          ROLE_LABEL[u.role] || u.role,
        Location:      u.locationAddress || '—',
        ReferralCode:  u.referralCode || '—',
        TotalReferrals: u.referralCount || 0,
        Joined:        new Date(u.createdAt).toLocaleDateString('en-IN'),
      }));
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => '"' + String(r[h]).replace(/"/g,'""') + '"').join(','))].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
      a.download = 'referrals_' + Date.now() + '.csv';
      a.click();
      toast.success('Exported!');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const filtered = topReferrers.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.mobile?.includes(search)
  );

  return (
    <AdminLayout title="Referral Management">

      {/* Stats */}
      {stats && (
        <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:20 }}>
          {[
            { label:'Total Referrals', value:stats.totalReferrals,    icon:'🔗' },
            { label:'Top Referrers',   value:stats.totalReferrers,    icon:'🏆' },
            { label:'Farmer Refs',     value:stats.farmerReferrals,   icon:'' },
            { label:'Agent Refs',      value:stats.agentReferrals,    icon:'🤝' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-card__icon">{s.icon}</div>
              <div className="stat-card__label">{s.label}</div>
              <div className="stat-card__value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex-between" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:8 }}>
          <input className="input" placeholder="Search name, mobile..." value={search}
            onChange={e => setSearch(e.target.value)} style={{ width:220 }} />
          <select className="input" value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); }} style={{ width:140 }}>
            <option value="">All Roles</option>
            <option value="seller">Farmers</option>
            <option value="buyer">Buyers</option>
            <option value="agent">Agents</option>
          </select>
        </div>
        <button className="btn btn--outline btn--sm" onClick={exportCSV} disabled={exporting}>
          {exporting ? <span className="spinner spinner--sm" /> : '📊 Export CSV'}
        </button>
      </div>

      {/* Leaderboard */}
      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>#</th>
                <th>User</th>
                <th>Role</th>
                <th>Location</th>
                <th>Referral Code</th>
                <th style={{ textAlign:'center' }}>Referrals</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state__icon">🔗</div>
                      <div className="empty-state__title">No referrals yet</div>
                    </div>
                  </td></tr>
                ) : filtered.map((u, i) => (
                  <React.Fragment key={u._id}>
                    <tr style={{ background: i < 3 ? '#FFFBEB' : 'white' }}>
                      <td style={{ fontWeight:700, color: i===0?'#F59E0B': i===1?'#94A3B8': i===2?'#CD7F32':'#64748B', fontSize:15 }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </td>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{u.name || '—'}</div>
                        <div style={{ fontSize:11, color:'#2563EB' }}>+91 {u.mobile}</div>
                      </td>
                      <td><span className={'badge ' + (ROLE_BADGE[u.role]||'badge--gray')}>{ROLE_LABEL[u.role]||u.role}</span></td>
                      <td style={{ fontSize:12, color:'#6C757D' }}>{u.locationAddress?.split(',')[0]||'—'}</td>
                      <td>
                        <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, letterSpacing:2, color:'#1A6B3C' }}>
                          {u.referralCode}
                        </span>
                      </td>
                      <td style={{ textAlign:'center' }}>
                        <span style={{ background:'#F0FDF4', color:'#16A34A', borderRadius:12, padding:'3px 10px', fontSize:13, fontWeight:700 }}>
                          🔗 {u.referralCount || 0}
                        </span>
                      </td>
                      <td style={{ fontSize:12 }}>{new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <button className="btn btn--outline btn--sm"
                          onClick={() => loadReferrals(u._id)}
                          disabled={refLoading === u._id}>
                          {refLoading === u._id
                            ? <span className="spinner spinner--sm" />
                            : expanded === u._id ? '▲ Hide' : '▼ View'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded referrals list */}
                    {expanded === u._id && referrals[u._id] && (
                      <tr>
                        <td colSpan={8} style={{ background:'#F8FAFC', padding:0 }}>
                          <div style={{ padding:'12px 16px' }}>
                            <div style={{ fontSize:12, fontWeight:600, color:'#64748B', marginBottom:10 }}>
                              USERS REFERRED BY {(u.name||'').toUpperCase()}
                            </div>
                            {referrals[u._id].length === 0 ? (
                              <div style={{ fontSize:13, color:'#94A3B8' }}>No referrals found</div>
                            ) : (
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px,1fr))', gap:8 }}>
                                {referrals[u._id].map(r => (
                                  <div key={r._id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:'white', borderRadius:8, border:'1px solid #E2E8F0' }}>
                                    <div style={{ width:28, height:28, borderRadius:'50%', background:'#3B82F6', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0 }}>
                                      {r.name?.[0]||'?'}
                                    </div>
                                    <div style={{ flex:1, minWidth:0 }}>
                                      <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name||'—'}</div>
                                      <div style={{ fontSize:11, color:'#64748B' }}>
                                        {ROLE_LABEL[r.role]} · {new Date(r.createdAt).toLocaleDateString('en-IN')}
                                      </div>
                                    </div>
                                    <span className={'badge ' + (r.isActive?'badge--success':'badge--danger')} style={{ fontSize:10 }}>
                                      {r.isActive?'Active':'Banned'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}